import { FC } from 'react';

import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { ExternalLink, Search } from 'lucide-react';

import { Button } from '@/components/TailwindComponents/base/Button';
import { MasterIndexType } from '@/pages/MasterIndex/components/MasterIndexDetailsPage/MasterIndexDetailsPage';

import { AnyMasterRecord, mergeConfigs } from './mergeFieldConfig';

interface MergeSearchStepProps {
  masterIndexType: MasterIndexType;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onSearchFocus: () => void;
  searchResults: AnyMasterRecord[];
  searchLoading: boolean;
  onSelect: (record: AnyMasterRecord) => void;
}

export const MergeSearchStep: FC<MergeSearchStepProps> = ({
  masterIndexType,
  searchQuery,
  onSearchChange,
  onSearchFocus,
  searchResults,
  searchLoading,
  onSelect,
}) => {
  const theme = useTheme();
  const config = mergeConfigs[masterIndexType];
  const TypeIcon = config.icon;

  const getRecordPath = (record: AnyMasterRecord): string => {
    const id = (record as any)._id ?? (record as any).id;
    switch (masterIndexType) {
      case 'organizations':
        return `/master-index/organizations/${id}/view/information`;
      case 'vehicles':
        return `/master-index/vehicles/${id}/information`;
      case 'address':
        return `/master-index/address/${id}/view/information`;
      case 'persons':
      default:
        return `/master-index/persons/${id}/view`;
    }
  };

  return (
    <Stack spacing={2}>
      <Typography fontWeight={500} fontSize={15}>
        Search for record to be merged
      </Typography>
      <Alert severity="info" variant="outlined">
        <Stack spacing={0.25}>
          <Typography fontSize={14}>
            Search for a record to merge into this one.
          </Typography>
          <Typography fontSize={14}>
            Selected record will be deleted once merged.
          </Typography>
        </Stack>
      </Alert>

      <TextField
        fullWidth
        size="small"
        placeholder={`Search ${masterIndexType}...`}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        onFocus={onSearchFocus}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {searchLoading ? (
                <CircularProgress size={16} />
              ) : (
                <Search size={16} color={theme.palette.text.secondary} />
              )}
            </InputAdornment>
          ),
        }}
      />

      {searchResults.length > 0 && (
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {searchResults.map((record, index) => {
            const id = (record as any)._id ?? (record as any).id;
            const title = config.getRecordTitle(record);
            const subtitle = config.getRecordSubtitle(record);
            return (
              <Box key={id}>
                {index > 0 && <Divider />}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1.5,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {title}
                    </Typography>
                    {subtitle && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        mt={0.25}
                      >
                        <TypeIcon
                          sx={{
                            fontSize: 14,
                            color: theme.palette.text.secondary,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          {subtitle}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    ml={1.5}
                    flexShrink={0}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onSelect(record)}
                    >
                      Select
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() =>
                        window.open(getRecordPath(record), '_blank')
                      }
                      title="Open in new tab"
                    >
                      <ExternalLink
                        size={16}
                        color={theme.palette.text.secondary}
                      />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}

      {searchQuery.trim() && !searchLoading && searchResults.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          py={2}
        >
          No records found
        </Typography>
      )}
    </Stack>
  );
};
